import * as React from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, Box } from '@mui/material';
import { getDashboard, getBazaarSummary } from '../app/store/actions/dataActions';
import { bazaarsEnabled } from '../app/config/featureFlags';
import { useEffect, useState } from 'react';
import moment from 'moment';
import Hoverable from '../app/glass/Hoverable';
import { cardShell, formatEgp, formatInt, pageTitle, redCta } from '../app/glass/styles';
import { MainInput } from '../app/components/Styles';

const mkCard = (label, value, sub, opts = {}) => ({
  label,
  value,
  sub: sub || false,
  badge: opts.badge || false,
  bg: 'var(--card-solid)',
  border: opts.hot ? 'rgba(190,50,62,0.35)' : 'var(--s10)',
  color: opts.hot ? 'var(--red-tx2)' : 'var(--tx)',
});

const tipBox = {
  position: 'absolute',
  zIndex: 5,
  pointerEvents: 'none',
  padding: '8px 12px',
  borderRadius: 12,
  background: 'var(--side-bg)',
  border: '1px solid var(--s12)',
  boxShadow: '0 12px 28px var(--sh45)',
  fontSize: 12.5,
  color: 'var(--tx)',
  whiteSpace: 'nowrap',
};

export const Home = ({ user, getDashboard, getBazaarSummary, dashboard, bazaarSummary, history, isLoading }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [startDate] = useState(moment().startOf('month'));
  const [endDate] = useState(moment().endOf('month'));
  const [target, setTarget] = useState(localStorage.getItem(`${user?._id}_target`) || '');
  const [menuOpen, setMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [chartTip, setChartTip] = useState(null);

  useEffect(() => {
    getDashboard({
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD'),
    });
    if (bazaarsEnabled) getBazaarSummary({});
  }, []);

  const selectTarget = (value) => {
    setTarget(value);
    localStorage.setItem(`${user?._id}_target`, value);
  };

  const orders = dashboard?.orders || {};
  const stocks = dashboard?.stocks || {};
  const returns = dashboard?.returnAnalysis || {};
  const cod = dashboard?.cod || {};
  const pipeline = orders.pipeline || {};
  const periodMix = orders.periodMix || {};
  const pending = pipeline.pending
    ?? ((orders.totalEnRoute || 0) + (orders.totalOther || 0));
  const income = Number(cod.totalIncome) || 0;
  const net = Number(cod.netIncome) || 0;
  const expectedCash = Number.isFinite(Number(cod.expectedIncome))
    ? Number(cod.expectedIncome)
    : 0;
  const tgt = Number(target);
  const hasTarget = Number.isFinite(tgt) && tgt > 0;
  const pct = hasTarget ? Math.min(100, Math.max(0, (income / tgt) * 100)) : 0;
  const netOfCodPct = income ? Math.round((net / income) * 100) : 0;
  const monthLabel = startDate.format('MMMM YYYY');

  const todayStats = [
    mkCard(t('home.inProgress'), formatInt(pipeline.inProgress || 0)),
    mkCard(t('home.headingToCustomer'), formatInt(pipeline.headingToCustomer || 0)),
    mkCard(t('home.completedOrders'), formatInt(orders.totalDelivered), `/ ${formatInt(orders.totalOrders)}`),
    mkCard(t('home.awaitingAction'), formatInt(pipeline.awaitingAction || 0), false, { hot: true }),
    mkCard(t('home.headingToYou'), formatInt(pipeline.headingToYou || 0)),
    mkCard(t('home.newOrders'), formatInt(pipeline.new || 0)),
    mkCard(t('home.expectedCash'), formatEgp(expectedCash), 'EGP'),
    mkCard(t('home.collectedCash'), formatEgp(income), 'EGP', { badge: income ? `${netOfCodPct}%` : false }),
  ];

  const returnReasons = [
    { label: t('home.customerIssue'), count: returns.totalCustomerErrors || 0 },
    { label: t('home.defectiveItem'), count: returns.totalDefective || 0 },
    { label: t('home.wrongItem'), count: returns.totalWarehouseMistake || 0 },
  ];
  const maxReason = Math.max(1, ...returnReasons.map((r) => r.count));

  const hasPeriodMix = Boolean(orders.periodMix);
  const delivered = Number(hasPeriodMix ? periodMix.delivered : orders.totalDelivered) || 0;
  const inProgressMix = Number(hasPeriodMix ? periodMix.inProgress : orders.totalFulfilled) || 0;
  const enRoute = Number(hasPeriodMix ? periodMix.headingToCustomer : orders.totalEnRoute) || 0;
  const returnedInTransit = Number(hasPeriodMix ? periodMix.headingToYou : 0) || 0;
  const returnedSettled = Number(hasPeriodMix ? periodMix.returnedToChainz : 0) || 0;
  const returned = hasPeriodMix
    ? returnedInTransit + returnedSettled
    : (Number(orders.totalReturned) || 0);
  const cancelled = Number(hasPeriodMix ? periodMix.cancelled : orders.totalCancelled) || 0;
  const otherBucket = hasPeriodMix
    ? (Number(periodMix.new) || 0) + (Number(periodMix.awaitingAction) || 0) + (Number(periodMix.other) || 0)
    : ((Number(orders.totalOther) || 0) + (Number(orders.totalSoldOutOrders) || 0));
  const totalOrders = Number(orders.totalOrders) || 0;

  const donutSegments = [
    { key: 'delivered', label: t('home.delivered'), value: delivered, color: '#7D010C' },
    { key: 'enRoute', label: t('home.enRoute'), value: enRoute, color: '#c0303e' },
    { key: 'fulfilled', label: t('home.inProgress'), value: inProgressMix, color: '#e2707c' },
    { key: 'returned', label: t('home.returnedOrders'), value: returned, color: '#ACBBC6' },
    { key: 'cancelled', label: t('home.cancelled'), value: cancelled, color: '#6d6480' },
    { key: 'other', label: t('home.other'), value: otherBucket, color: '#9c97b2' },
  ];
  const donutSum = donutSegments.reduce((a, s) => a + s.value, 0);
  const donutTotal = Math.max(1, totalOrders || donutSum);
  const donutR = 70;
  const donutCirc = 2 * Math.PI * donutR;
  let donutOffset = 0;
  const donutArcs = donutSegments.map((seg) => {
    const len = (seg.value / donutTotal) * donutCirc;
    const arc = { ...seg, dash: len, offset: -donutOffset, pct: donutTotal ? Math.round((seg.value / donutTotal) * 1000) / 10 : 0 };
    donutOffset += len;
    return arc;
  });

  const hist = Array.isArray(history) ? history : [];
  const lastSales = hist.length ? hist[hist.length - 1]?.sales : income;
  const lastOrders = hist.length ? hist[hist.length - 1]?.orders : orders.totalOrders;

  const chartPts = hist.slice(-9);
  const maxOrd = Math.max(1, ...chartPts.map((h) => Number(h.orders) || 0), 1);
  const maxSales = Math.max(1, ...chartPts.map((p) => Number(p.sales) || 0), 1);
  const fulfillmentPts = chartPts.map((h, i) => {
    const x = 80 + i * (480 / Math.max(chartPts.length - 1, 1));
    const y = 180 - ((Number(h.orders) || 0) / maxOrd) * 150;
    return { x, y, value: Number(h.orders) || 0, label: moment(h.date).format('MMM YYYY'), raw: h };
  });
  const linePath = fulfillmentPts.length
    ? fulfillmentPts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x} ${p.y}`).join(' ')
    : '';

  const salesPts = chartPts.map((h, i) => {
    const x = 16 + i * (248 / Math.max(chartPts.length - 1, 1));
    const y = 112 - ((Number(h.sales) || 0) / maxSales) * 100;
    return { x, y, value: Number(h.sales) || 0, label: moment(h.date).format('MMM YYYY'), raw: h };
  });
  const salesPath = salesPts.length
    ? salesPts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x} ${p.y}`).join(' ')
    : '';

  return (
    <>
      <div className="cz-wrap" style={{ display: 'flex', alignItems: 'center', gap: 24, marginTop: 6 }}>
        <div>
          <div style={{ fontSize: 14, color: 'var(--mut2)' }}>{t('home.welcomeBack')}</div>
          <div style={pageTitle}>{user?.name || t('navigation.partner')}</div>
        </div>
        <Hoverable
          onClick={() => navigate('/inventory?tab=send')}
          base={{ ...redCta, marginInlineStart: 'auto' }}
          hover={{ background: 'linear-gradient(160deg, rgba(156,4,16,0.65), rgba(100,1,10,0.50))' }}
        >
          {t('navigation.sendInventory')}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 3 10 14" />
            <path d="M21 3 14.5 21 10 14l-7-4.5z" />
          </svg>
        </Hoverable>
      </div>

      <div
        className="cz-g2"
        style={{
          borderRadius: 26,
          padding: '26px 34px',
          background: 'var(--card-solid)',
          border: '1px solid var(--s12)',
          boxShadow: 'inset 0 1px 0 var(--s14), 0 22px 50px var(--sh45)',
          display: 'grid',
          gridTemplateColumns: '1.1fr 1fr 1fr 1fr',
          gap: 24,
          alignItems: 'center',
        }}
      >
        <div>
          <div style={{ fontSize: 12, color: 'var(--mut2)', marginBottom: 10 }}>{t('home.netIncome')}</div>
          <div style={{ fontSize: 38, fontWeight: 500 }}>
            {isLoading ? '—' : formatEgp(net)} <span style={{ fontSize: 17, color: 'var(--tx2)' }}>EGP</span>
          </div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: 'var(--mut2)', marginBottom: 10 }}>{t('home.totalStocks')}</div>
          <div style={{ fontSize: 30, fontWeight: 500 }}>
            {formatInt(stocks.totalStockItems)} <span style={{ fontSize: 15, color: 'var(--tx2)' }}>{t('home.items')}</span>
          </div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: 'var(--mut2)', marginBottom: 10 }}>{t('home.pendingOrders')}</div>
          <div style={{ fontSize: 30, fontWeight: 500 }}>
            {formatInt(pending)} <span style={{ fontSize: 15, color: 'var(--tx2)' }}>{t('home.orders')}</span>
          </div>
        </div>
        <div style={{ justifySelf: 'end', textAlign: 'end' }}>
          <div style={{ fontSize: 12, color: 'var(--mut2)', marginBottom: 10 }}>{t('home.returnedOrders')}</div>
          <div style={{ fontSize: 30, fontWeight: 500 }}>
            {formatInt(returns.totalReturns)} <span style={{ fontSize: 15, color: 'var(--tx2)' }}>{t('home.orders')}</span>
          </div>
        </div>
      </div>

      <div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12 }}>
          <div style={{ fontSize: 15, fontWeight: 600 }}>{t('home.pipelineSummary')}</div>
          <div style={{ fontSize: 11, color: 'var(--mut)' }}>{t('home.pipelineHint', { month: monthLabel })}</div>
        </div>
        <div className="cz-g2" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
          {todayStats.map((st) => (
            <div
              key={st.label}
              style={{
                borderRadius: 20,
                padding: '18px 20px',
                background: st.bg,
                border: `1px solid ${st.border}`,
                boxShadow: 'inset 0 1px 0 var(--s10), 0 14px 34px var(--sh40)',
              }}
            >
              <div style={{ fontSize: 12, color: 'var(--mut)', marginBottom: 10 }}>{st.label}</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
                <div style={{ fontSize: 26, fontWeight: 600, color: st.color }}>{st.value}</div>
                {st.sub && <div style={{ fontSize: 13, color: 'var(--mut)' }}>{st.sub}</div>}
                {st.badge && (
                  <div title={t('home.netOfCod')} style={{ marginInlineStart: 'auto', fontSize: 10.5, fontWeight: 600, padding: '3px 9px', borderRadius: 999, background: 'rgba(125,1,12,0.30)', border: '1px solid rgba(190,50,62,0.40)', color: 'var(--red-tx)' }}>
                    {st.badge}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="cz-g1" style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: 20, alignItems: 'stretch' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div style={cardShell}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
              <div style={{ fontSize: 15, fontWeight: 600 }}>{t('home.salesTargetProgress')}</div>
              <Hoverable
                base={{ marginInlineStart: 'auto', fontSize: 12, color: 'var(--tx2)', textDecoration: 'underline', cursor: 'pointer' }}
                hover={{ color: 'var(--txs)' }}
                onClick={(e) => { setAnchorEl(e.currentTarget); setMenuOpen(true); }}
              >
                {t('common.edit')}
              </Hoverable>
            </div>
            <Menu anchorEl={anchorEl} open={menuOpen} onClose={() => setMenuOpen(false)}>
              <Box sx={{ padding: 2, minWidth: 220 }}>
                <MainInput type="number" label={t('home.salesTargetEGP')} value={target} onChange={({ target: el }) => selectTarget(el.value)} inputProps={{ min: 0 }} />
              </Box>
            </Menu>
            <div style={{ fontSize: 26, fontWeight: 600 }}>{hasTarget ? `${pct.toFixed(1)}%` : '—'}</div>
            <div style={{ fontSize: 12, color: 'var(--mut)', marginTop: 2, marginBottom: 14 }}>
              {hasTarget ? t('home.ofTarget', { amount: formatEgp(tgt) }) : t('home.salesTargetEGP')}
            </div>
            <div
              style={{ position: 'relative', height: 12, borderRadius: 999, background: 'rgba(125,1,12,0.25)', border: '1px solid var(--s06)', overflow: 'visible', cursor: 'pointer' }}
              onMouseEnter={() => setChartTip({ id: 'salesTarget' })}
              onMouseLeave={() => setChartTip((tip) => (tip?.id === 'salesTarget' ? null : tip))}
            >
              {chartTip?.id === 'salesTarget' && (
                <div style={{ ...tipBox, bottom: 'calc(100% + 8px)', left: '50%', transform: 'translateX(-50%)' }}>
                  <div style={{ fontWeight: 600 }}>{t('home.thisMonthSales')}</div>
                  <div style={{ color: 'var(--tx2)', marginTop: 2 }}>{formatEgp(income)} EGP</div>
                </div>
              )}
              <div style={{ height: 12, borderRadius: 999, overflow: 'hidden' }}>
                <div style={{ width: `${hasTarget ? pct : 0}%`, height: '100%', borderRadius: 999, background: 'linear-gradient(90deg, #7D010C, #c0303e)', boxShadow: '0 0 12px rgba(192,48,62,0.6)' }} />
              </div>
            </div>
          </div>

          <div
            onClick={() => bazaarsEnabled && navigate('/bazaars')}
            style={{
              flex: 1,
              ...cardShell,
              cursor: bazaarsEnabled ? 'pointer' : 'default',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <div style={{ fontSize: 15, fontWeight: 600 }}>{t('bazaars.title')}</div>
              {!bazaarsEnabled && (
                <div style={{ fontSize: 10, padding: '3px 10px', borderRadius: 999, background: 'rgba(125,1,12,0.45)', border: '1px solid rgba(190,50,62,0.45)', color: 'var(--red-tx2)', fontWeight: 600, letterSpacing: '0.06em' }}>
                  SOON
                </div>
              )}
            </div>
            {bazaarsEnabled ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                <div>
                  <div style={{ fontSize: 11, color: 'var(--mut)' }}>{t('bazaars.dashboardActive')}</div>
                  <div style={{ fontSize: 18, fontWeight: 600 }}>{bazaarSummary?.inProgressCount || 0}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: 'var(--mut)' }}>{t('bazaars.dashboardRevenue')}</div>
                  <div style={{ fontSize: 18, fontWeight: 600 }}>{formatInt(bazaarSummary?.totalRevenue || 0)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: 'var(--mut)' }}>{t('bazaars.dashboardUnits')}</div>
                  <div style={{ fontSize: 18, fontWeight: 600 }}>{bazaarSummary?.unitsSold || 0}</div>
                </div>
              </div>
            ) : (
              <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--tx2)' }}>{t('bazaars.comingSoonDescription')}</div>
            )}
          </div>
        </div>

        <div style={{ ...cardShell, borderRadius: 26, padding: '24px 28px', minWidth: 0, position: 'relative' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
            <div>
              <div style={{ fontSize: 17, fontWeight: 600 }}>{t('home.fulfillmentTrends')}</div>
              <div style={{ fontSize: 11, color: 'var(--mut)', marginTop: 3 }}>{t('home.completedPerMonth')}</div>
            </div>
            <div style={{ marginInlineStart: 'auto', fontSize: 11, padding: '5px 12px', borderRadius: 999, background: 'var(--s08)', border: '1px solid var(--s12)', color: 'var(--tx3)' }}>
              {formatInt(lastOrders)} {t('home.orders')}
            </div>
          </div>
          <div style={{ position: 'relative' }}>
            {chartTip?.id === 'fulfillment' && (
              <div style={{ ...tipBox, top: Math.max(8, chartTip.y - 48), left: Math.min(chartTip.x + 8, 420) }}>
                <div style={{ fontWeight: 600 }}>{chartTip.label}</div>
                <div style={{ color: 'var(--tx2)', marginTop: 2 }}>{formatInt(chartTip.value)} {t('home.orders')}</div>
              </div>
            )}
            <svg
              viewBox="0 0 640 210"
              style={{ width: '100%', display: 'block' }}
              onMouseLeave={() => setChartTip((tip) => (tip?.id === 'fulfillment' ? null : tip))}
            >
              <g stroke="var(--s06)" strokeWidth="1">
                <line x1="44" y1="20" x2="635" y2="20" />
                <line x1="44" y1="60" x2="635" y2="60" />
                <line x1="44" y1="100" x2="635" y2="100" />
                <line x1="44" y1="140" x2="635" y2="140" />
                <line x1="44" y1="180" x2="635" y2="180" />
              </g>
              <g fill="var(--mut)" fontSize="10" textAnchor="end">
                <text x="38" y="24">{formatInt(maxOrd)}</text>
                <text x="38" y="184">0</text>
              </g>
              {linePath ? (
                <path d={linePath} fill="none" stroke="#c0303e" strokeWidth="2.5" strokeLinecap="round" style={{ animation: 'dashPulse 4s ease-in-out infinite' }} />
              ) : null}
              {fulfillmentPts.map((p, i) => (
                <g key={i}>
                  <circle
                    cx={p.x}
                    cy={p.y}
                    r={chartTip?.id === 'fulfillment' && chartTip.index === i ? 6 : 4}
                    fill="#c0303e"
                    stroke="var(--badge-ring)"
                    strokeWidth="2"
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={() => setChartTip({ id: 'fulfillment', index: i, x: p.x, y: p.y, label: p.label, value: p.value })}
                  />
                  <circle
                    cx={p.x}
                    cy={p.y}
                    r="14"
                    fill="transparent"
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={() => setChartTip({ id: 'fulfillment', index: i, x: p.x, y: p.y, label: p.label, value: p.value })}
                  />
                </g>
              ))}
              <g fill="var(--mut)" fontSize="11" textAnchor="middle">
                {fulfillmentPts.map((p, i) => (
                  <text key={i} x={p.x} y="202">{moment(chartPts[i]?.date).format('MMM')}</text>
                ))}
              </g>
            </svg>
          </div>
        </div>
      </div>

      <div className="cz-g1" style={{ display: 'grid', gridTemplateColumns: '1fr 1.15fr 1fr', gap: 20, alignItems: 'stretch' }}>
        <div style={cardShell}>
          <div style={{ fontSize: 15, fontWeight: 600 }}>{t('home.returnsAnalysis')}</div>
          <div style={{ fontSize: 11, color: 'var(--mut)', marginTop: 3, marginBottom: 16 }}>{t('home.returnReasonsOrder')}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {returnReasons.map((reason) => (
              <div
                key={reason.label}
                title={`${reason.label}: ${reason.count}`}
                onMouseEnter={() => setChartTip({ id: 'returns', label: reason.label, value: reason.count })}
                onMouseLeave={() => setChartTip((tip) => (tip?.id === 'returns' ? null : tip))}
              >
                <div style={{ display: 'flex', alignItems: 'baseline', fontSize: 12.5, color: 'var(--tx2)', marginBottom: 7 }}>
                  {reason.label}
                  <span style={{ marginInlineStart: 'auto', color: 'var(--tx)', fontWeight: 600 }}>{reason.count}</span>
                </div>
                <div style={{ height: 8, borderRadius: 999, background: 'rgba(125,1,12,0.22)', overflow: 'hidden' }}>
                  <div style={{ width: `${(reason.count / maxReason) * 100}%`, height: '100%', borderRadius: 999, background: 'linear-gradient(90deg, #7D010C, #c0303e)' }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ ...cardShell, display: 'flex', flexDirection: 'column', minHeight: 320 }}>
          <div style={{ fontSize: 15, fontWeight: 600 }}>{t('home.ordersFulfillment')}</div>
          <div style={{ fontSize: 11, color: 'var(--mut)', marginTop: 3, marginBottom: 12 }}>
            {formatInt(totalOrders)} {t('home.ordersThisMonth')}
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 18, minHeight: 0 }}>
            <div style={{ position: 'relative', width: 'min(100%, 220px)', aspectRatio: '1' }}>
              {chartTip?.id === 'donut' && (
                <div style={{ ...tipBox, top: '50%', left: '50%', transform: 'translate(-50%, -120%)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 3, background: chartTip.color }} />
                    <span style={{ fontWeight: 600 }}>{chartTip.label}</span>
                  </div>
                  <div style={{ color: 'var(--tx2)', marginTop: 4 }}>
                    {formatInt(chartTip.value)} · {chartTip.pct}%
                  </div>
                </div>
              )}
              <svg viewBox="0 0 180 180" style={{ width: '100%', height: '100%', display: 'block' }}>
                <g transform="translate(90, 90)">
                  <circle r={donutR} fill="none" stroke="var(--s08)" strokeWidth="22" />
                  {donutArcs.filter((a) => a.value > 0).map((arc) => (
                    <circle
                      key={arc.key}
                      r={donutR}
                      fill="none"
                      stroke={arc.color}
                      strokeWidth={chartTip?.id === 'donut' && chartTip.key === arc.key ? 26 : 22}
                      strokeDasharray={`${Math.max(arc.dash, 0.01)} ${donutCirc}`}
                      strokeDashoffset={arc.offset}
                      transform="rotate(-90)"
                      strokeLinecap="butt"
                      style={{ cursor: 'pointer', transition: 'stroke-width .15s ease' }}
                      onMouseEnter={() => setChartTip({ id: 'donut', key: arc.key, label: arc.label, value: arc.value, pct: arc.pct, color: arc.color })}
                      onMouseLeave={() => setChartTip((tip) => (tip?.id === 'donut' ? null : tip))}
                    />
                  ))}
                  <text y="-4" textAnchor="middle" fill="var(--tx)" fontSize="26" fontWeight="600">
                    {formatInt(chartTip?.id === 'donut' ? chartTip.value : totalOrders)}
                  </text>
                  <text y="16" textAnchor="middle" fill="var(--mut)" fontSize="11">
                    {chartTip?.id === 'donut' ? chartTip.label : t('home.orders')}
                  </text>
                </g>
              </svg>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', width: '100%', fontSize: 12, color: 'var(--tx2)' }}>
              {donutArcs.map((arc) => (
                <div
                  key={arc.key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '6px 8px',
                    borderRadius: 10,
                    background: chartTip?.id === 'donut' && chartTip.key === arc.key ? 'var(--s08)' : 'transparent',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={() => setChartTip({ id: 'donut', key: arc.key, label: arc.label, value: arc.value, pct: arc.pct, color: arc.color })}
                  onMouseLeave={() => setChartTip((tip) => (tip?.id === 'donut' ? null : tip))}
                >
                  <div style={{ width: 9, height: 9, borderRadius: 3, background: arc.color, flexShrink: 0 }} />
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{arc.label}</span>
                  <span style={{ fontWeight: 600, color: 'var(--tx)' }}>{formatInt(arc.value)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={{ ...cardShell, position: 'relative', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'baseline' }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 600 }}>{t('home.salesTrends')}</div>
              <div style={{ fontSize: 11, color: 'var(--mut)', marginTop: 3 }}>{t('home.codPerMonth')}</div>
            </div>
            <div style={{ marginInlineStart: 'auto', fontSize: 20, fontWeight: 600 }}>
              {chartTip?.id === 'sales' ? formatEgp(chartTip.value) : formatEgp(lastSales)}
            </div>
          </div>
          {chartTip?.id === 'sales' && (
            <div style={{ fontSize: 11, color: 'var(--mut)', marginTop: 4 }}>{chartTip.label}</div>
          )}
          <div style={{ flex: 1, position: 'relative', marginTop: 14, minHeight: 140 }}>
            <svg
              viewBox="0 0 280 140"
              style={{ width: '100%', height: '100%', display: 'block' }}
              onMouseLeave={() => setChartTip((tip) => (tip?.id === 'sales' ? null : tip))}
            >
              <defs>
                <linearGradient id="salesFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="rgba(192,48,62,0.35)" />
                  <stop offset="100%" stopColor="rgba(192,48,62,0)" />
                </linearGradient>
              </defs>
              {salesPath ? (
                <>
                  <path d={`${salesPath} L 264 130 L 16 130 Z`} fill="url(#salesFill)" />
                  <path d={salesPath} fill="none" stroke="#c0303e" strokeWidth="2.5" strokeLinecap="round" style={{ animation: 'dashPulse 4s ease-in-out infinite' }} />
                </>
              ) : null}
              {salesPts.map((p, i) => (
                <g key={i}>
                  <circle
                    cx={p.x}
                    cy={p.y}
                    r={chartTip?.id === 'sales' && chartTip.index === i ? 6 : 4}
                    fill="#c0303e"
                    stroke="var(--badge-ring)"
                    strokeWidth="2"
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={() => setChartTip({ id: 'sales', index: i, label: p.label, value: p.value })}
                  />
                  <circle
                    cx={p.x}
                    cy={p.y}
                    r="14"
                    fill="transparent"
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={() => setChartTip({ id: 'sales', index: i, label: p.label, value: p.value })}
                  />
                </g>
              ))}
            </svg>
          </div>
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  user: state?.auth?.user,
  isLoading: state?.records?.isLoading,
  history: state?.records?.history,
  dashboard: state?.records?.dashboard,
  bazaarSummary: state?.records?.bazaarSummary,
});

export default connect(mapStateToProps, { getDashboard, getBazaarSummary })(Home);
